import React from 'react';
import styled, { css } from 'styled-components/macro';
import { uiColors } from '../../common/styles';
import { Title } from '../studio/drawer-content/styled';
import './FramiButtonStyles.css';
import { useAppContext } from '../../context';

const wrapperStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    transition: 0.3s all ease-in-out;
    font-size: 1.6em;
    font-weight: 700;
    padding: 8px 12px;
    border-radius: 12px;
    &:hover {
        opacity: 85%;
    }
    &:disabled {
        opacity: 70%;
        cursor: unset;
    }
    margin-top: 8px;
    margin-bottom: 8px;
`;

const getVariantStyles = (variant) => {
    const styles = {
        primary: css`
            background-color: ${uiColors.PRIMARY_500};
            border: 1px solid ${uiColors.PRIMARY_500};
            color: ${uiColors.WHITE};
        `,
        secondary: css`
            background-color: ${uiColors.WHITE};
            border: 1px solid ${uiColors.GRAY_200};
            color: ${uiColors.PRIMARY_500};
        `,
        success: css`
            background-color: ${uiColors.GREEN_50};
            color: ${uiColors.PRIMARY_TEXT};
        `,
        warning: css`
            background-color: ${uiColors.WARNING_50};
            color: ${uiColors.PRIMARY_TEXT};
        `,
        info: css`
            background-color: ${uiColors.GRAY_50};
            color: ${uiColors.PRIMARY_TEXT};
        `,
        disabled: css`
            background-color: ${uiColors.GRAY_300};
            border: 1px solid ${uiColors.GRAY_300};
            color: ${uiColors.WHITE};
            cursor: auto;
        `,
        text: css`
            color: ${uiColors.PRIMARY_500};
        `,
    };
    return styles[variant];
};

const Button = styled.button`
    ${wrapperStyles}
    ${(props) => getVariantStyles(props.$variant)}
`;
const Anchor = styled.a`
    ${wrapperStyles}
    ${(props) => getVariantStyles(props.$variant)}
`;

const AdditionalTitle = styled(Title)`
    font-size: 12px !important;
    display: flex;
    justify-content: center;
    line-height: 16px;
    text-transform: none;
`;

const FramiButton = ({
    variant,
    leftIcon,
    rightIcon,
    onClick,
    title,
    additionalTitle,
    asAnchor,
    href,
    style,
    disabled,
    buttonType,
    className,
    additionalClassName,
    loading,
    dataCy,
    dataPh,
    iconShowMinWidth=150,
}) => {

    const { smallHeightDevice } = useAppContext();
    const handleClick = (event) => {
        onClick && onClick(event);
    };

    const [showIcons, setShowIcons] = React.useState(true);
    const wrapperRef = React.useRef(null);

    React.useEffect(() => {
        if (wrapperRef.current && iconShowMinWidth) {
            setShowIcons(wrapperRef.current.offsetWidth > iconShowMinWidth);
        }
    }, [iconShowMinWidth]);

    React.useEffect(() => {
        if (!wrapperRef.current || !iconShowMinWidth) return;

        // Initial measure
        setShowIcons(wrapperRef.current.offsetWidth > iconShowMinWidth);

        if ('ResizeObserver' in window) {
            const observer = new ResizeObserver(([entry]) => {
                setShowIcons(entry.contentRect.width > iconShowMinWidth);
            });
            observer.observe(wrapperRef.current);
            return () => observer.disconnect();
        }
    }, [iconShowMinWidth]);

    const Wrapper = asAnchor ? Anchor : Button;

    const titleStyles = style?.title || {};

    const wrapperProps = asAnchor ? { href } : { onClick: handleClick, type: buttonType || 'button' };
    return (
        <Wrapper
            ref={wrapperRef}
            className={`${className || 'frami-btn'} ${additionalClassName ? additionalClassName : ''}`}
            $variant={variant}
            style={style || {pointerEvents: 'auto'}}
            disabled={disabled || loading}
            data-cy={dataCy}
            {...wrapperProps}
        >
            {showIcons && leftIcon}
            <Title
                className="buttonTitle"
                $variant={variant}
                $fontWeight={700}
                $fontSize={style?.fontSize ?? (smallHeightDevice ? 12 : 14)}
                style={titleStyles}
                $withLeftMargin={!!leftIcon}
                $withRightMargin={!!rightIcon}
                $hasRightIcon={!!rightIcon}
            >
                {title}
                {loading && '...'}
                {additionalTitle && <AdditionalTitle $variant={variant}>{additionalTitle}</AdditionalTitle>}
            </Title>
            {showIcons && rightIcon}
        </Wrapper>
    );
};

export default FramiButton;
